import React from 'react';
import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import { RiInformationLine } from '@remixicon/react';
import { formatNumber } from '../../utils/customFormat';
import { DeliveryHealthCheckAggregates_T } from '../../types/Delivery';
import SummaryCard from '../UI/SummaryCard';
import { HEALTH_CHECK_CATEGORIES } from '../../utils/healthCheckRenderers';

interface DeliveryHealthCheckSummaryCardsProps {
  loading?: boolean;
  onCardClick?: (filterType: string) => void;
  selectedFilter?: string;
  dynamicStats: DeliveryHealthCheckAggregates_T | null;
  activeOnly?: boolean;
  onToggleActiveOnly?: () => void;  // For suspicious inactive card behavior
}

// Helper component to wrap cards with info tooltip
interface CardWithTooltipProps {
  children: React.ReactNode;
  category: keyof typeof HEALTH_CHECK_CATEGORIES;
  isSelected: boolean;
  hasIssues: boolean;
  onClick: () => void;
}

const CardWithTooltip: React.FC<CardWithTooltipProps> = ({ 
  children, 
  category, 
  isSelected, 
  hasIssues,
  onClick 
}) => {
  const categoryInfo = HEALTH_CHECK_CATEGORIES[category];
  
  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
        {categoryInfo.title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic', color: 'inherit' }}>
        {categoryInfo.description}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'inherit' }}>
        Specific checks:
      </Typography>
      <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
        {categoryInfo.checks.map((check, idx) => (
          <Typography 
            key={idx} 
            component="li" 
            variant="caption" 
            sx={{ mb: 0.5, lineHeight: 1.3, color: 'inherit' }}
          >
            {check}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <Box
        onClick={onClick}
        sx={{ 
          opacity: isSelected ? 1 : (hasIssues ? 0.8 : 0.7),
          transform: isSelected ? 'scale(1.02)' : 'scale(1)',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {children}
      </Box>
      <Tooltip 
        title={tooltipContent} 
        placement="top"
        arrow
        enterDelay={200}
      >
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 20,
            height: 20,
            padding: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
            zIndex: 2,
          }}
          onClick={(e) => e.stopPropagation()} // Prevent card click when clicking info icon
        >
          <RiInformationLine size={14} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const DeliveryHealthCheckSummaryCards: React.FC<DeliveryHealthCheckSummaryCardsProps> = ({
  loading = false,
  onCardClick,
  selectedFilter = 'all',
  dynamicStats,
  activeOnly = true,
  onToggleActiveOnly
}) => {
  // Category counts
  const deliveryTimelineCount = dynamicStats?.total_deliveries_with_delivery_timeline_issues || 0;
  const licenseStatusCount = dynamicStats?.total_deliveries_with_license_status_issues || 0;
  const volumeUpgradesCount = dynamicStats?.total_deliveries_with_volume_upgrades_issues || 0;
  const fulfillmentSetupCount = dynamicStats?.total_deliveries_with_fulfillment_setup_issues || 0;
  const suspiciousInactiveCount = dynamicStats?.total_suspicious_inactive_deliveries || 0;

  // Handler for suspicious inactive card - auto-toggles active-only(off) if needed
  const handleSuspiciousInactiveClick = () => {
    if (activeOnly && onToggleActiveOnly) {
      // First toggle off active-only
      onToggleActiveOnly();
      // Apply the filter
      setTimeout(() => {
        onCardClick?.('suspicious_inactive');
      }, 0);
    } else {
      // Already showing inactive, just apply filter
      onCardClick?.('suspicious_inactive');
    }
  };

  // Loading state show skeleton cards
  if (loading || !dynamicStats) {
    return (
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <SummaryCard
            key={i}
            title="Loading..."
            value="--"
            metricType="info"
          />
        ))}
      </Box>
    );
  }

  // Get the total deliveries with issues
  const deliveriesWithIssuesCount = dynamicStats.total_deliveries_with_issues;
  const deliveryHasIssues = deliveriesWithIssuesCount > 0;

  // Calculate healthy deliveries metrics
  const totalDeliveries = dynamicStats.total_active_deliveries;
  const deliveriesWithIssuesForCalc = dynamicStats.total_deliveries_with_issues;
  const healthyDeliveriesCount = totalDeliveries - deliveriesWithIssuesForCalc;
  const healthyPercentage = totalDeliveries > 0 ? (healthyDeliveriesCount / totalDeliveries) * 100 : 0;
  
  // Dynamic label for the healthy card
  const healthyLabel = activeOnly ? 'Healthy Active Deliveries' : 'Healthy Deliveries';
  const healthySubtitle = activeOnly 
    ? `${formatNumber(healthyDeliveriesCount)} of ${formatNumber(totalDeliveries)} active deliveries`
    : `${formatNumber(healthyDeliveriesCount)} of ${formatNumber(totalDeliveries)} deliveries`;

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* All Issues Card */}
        <Box 
          onClick={() => onCardClick?.('all')}
          sx={{ 
            opacity: selectedFilter === 'all' ? 1 : (deliveryHasIssues ? 0.8 : 0.7),
            transform: selectedFilter === 'all' ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title="Deliveries with issues"
            value={formatNumber(deliveriesWithIssuesCount || 0)}
            subtitle={deliveryHasIssues ? 'Deliveries requiring attention' : 'No issues'}
            metricType="issues"
            clickable={deliveryHasIssues}
            isActive={selectedFilter === 'all'}
          />
        </Box>

        {/* Healthy Deliveries Card */}
        <Box 
          sx={{ 
            cursor: 'default',
            opacity: 0.9,
            transform: 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <SummaryCard
            title={healthyLabel}
            value={`${healthyPercentage.toFixed(1)}%`}
            subtitle={healthySubtitle}
            metricType="success"
          />
        </Box>

        {/* Delivery Timeline Issues Card */}
        <CardWithTooltip
          category="delivery_timeline"
          isSelected={selectedFilter === 'delivery_timeline'}
          hasIssues={deliveryTimelineCount > 0}
          onClick={() => onCardClick?.('delivery_timeline')}
        >
          <SummaryCard
            title="Delivery Timeline Issues"
            value={formatNumber(deliveryTimelineCount)}
            subtitle="Dates missing, invalid, or misaligned"
            metricType="date_related"
            clickable={deliveryTimelineCount > 0}
            isActive={selectedFilter === 'delivery_timeline'}
          />
        </CardWithTooltip>

        {/* License & Status Consistency Card */}
        <CardWithTooltip
          category="license_status"
          isSelected={selectedFilter === 'license_status'}
          hasIssues={licenseStatusCount > 0}
          onClick={() => onCardClick?.('license_status')}
        >
          <SummaryCard
            title="License & Status Consistency"
            value={formatNumber(licenseStatusCount)}
            subtitle="License dates or active status mismatches"
            metricType="requirement"
            clickable={licenseStatusCount > 0}
            isActive={selectedFilter === 'license_status'}
          />
        </CardWithTooltip>

        {/* Volume Upgrade Scheduling Card */}
        <CardWithTooltip
          category="volume_upgrades"
          isSelected={selectedFilter === 'volume_upgrades'}
          hasIssues={volumeUpgradesCount > 0}
          onClick={() => onCardClick?.('volume_upgrades')}
        >
          <SummaryCard
            title="Volume Upgrade Scheduling"
            value={formatNumber(volumeUpgradesCount)}
            subtitle="Upgrade existence and alignment issues"
            metricType="info"
            clickable={volumeUpgradesCount > 0}
            isActive={selectedFilter === 'volume_upgrades'}
          />
        </CardWithTooltip>

        {/* Fulfillment Setup Card */}
        <CardWithTooltip
          category="fulfillment_setup"
          isSelected={selectedFilter === 'fulfillment_setup'}
          hasIssues={fulfillmentSetupCount > 0}
          onClick={() => onCardClick?.('fulfillment_setup')}
        >
          <SummaryCard
            title="Fulfillment Setup"
            value={formatNumber(fulfillmentSetupCount)}
            subtitle="Structural or operational gaps"
            metricType="issues"
            clickable={fulfillmentSetupCount > 0}
            isActive={selectedFilter === 'fulfillment_setup'}
          />
        </CardWithTooltip>

        {/* Suspicious Inactive Deliveries Card - Always visible, smart behavior */}
        <Box 
          sx={{ 
            position: 'relative',
            display: 'inline-block',
            opacity: activeOnly 
              ? (suspiciousInactiveCount > 0 ? 0.6 : 0.5)  // Semi-transparent when active-only is ON
              : (selectedFilter === 'suspicious_inactive' ? 1 : (suspiciousInactiveCount > 0 ? 0.8 : 0.7)),
            transform: selectedFilter === 'suspicious_inactive' ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box onClick={handleSuspiciousInactiveClick}>
            <SummaryCard
              title="Suspicious Inactive Deliveries"
              value={formatNumber(suspiciousInactiveCount)}
              subtitle={activeOnly ? "Toggle to view details" : "Unproper deactivation process detected"}
              metricType="issues"
              clickable={suspiciousInactiveCount > 0}
              isActive={selectedFilter === 'suspicious_inactive'}
            />
          </Box>
          <Tooltip 
            title={
              <Box sx={{ maxWidth: 320 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
                  {HEALTH_CHECK_CATEGORIES.suspicious_inactive.title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic', color: 'inherit' }}>
                  {HEALTH_CHECK_CATEGORIES.suspicious_inactive.description}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'inherit' }}>
                  Specific checks:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
                  {HEALTH_CHECK_CATEGORIES.suspicious_inactive.checks.map((check, idx) => (
                    <Typography 
                      key={idx} 
                      component="li" 
                      variant="caption" 
                      sx={{ mb: 0.5, lineHeight: 1.3, color: 'inherit' }}
                    >
                      {check}
                    </Typography>
                  ))}
                </Box>
              </Box>
            } 
            placement="top"
            arrow
            enterDelay={200}
          >
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 20,
                height: 20,
                padding: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                zIndex: 2,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <RiInformationLine size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

export default DeliveryHealthCheckSummaryCards;

